// What a draft board tells a drafter about each player beyond his rank
// (PLAN-2026-10-14 §3): his place at his position, his tier, and his bye.
// Pure and sport-free, so the live room, the REST read and the simulator
// number the same board the same way.
//
// NUMBERS, NOT ADVICE. A tier is where the board's own values fall off a
// ledge, and a position rank is a count; neither says who to take
// (docs/decisions/2026-09-12-tools-not-instructions.md).

export interface BoardEntry {
  id: string;
  /** His primary position. */
  position: string;
  /** What the board ordered him on, best first; null for a player it had nothing for. */
  value: number | null;
}

/**
 * Each player's place among players at his own position, 1 for the first,
 * counted down the board in the order given. RB4 is the fourth running back
 * on this board.
 */
export function positionRanks(board: readonly BoardEntry[]): Map<string, number> {
  const seen = new Map<string, number>();
  const out = new Map<string, number>();
  for (const entry of board) {
    const n = (seen.get(entry.position) ?? 0) + 1;
    seen.set(entry.position, n);
    out.set(entry.id, n);
  }
  return out;
}

export interface TierOptions {
  /** How many at a position are tiered; everybody past them shares the tier after the last. */
  top?: number;
  /** About how many players a tier holds, which decides how many tiers there are. */
  tierSize?: number;
  /** The most tiers a position is cut into, before the rest. */
  maxTiers?: number;
}

/**
 * Tiers at each position, cut where the board's values fall furthest.
 *
 * WHERE THE LEDGES ARE, NOT TWELVE TO A TIER. A tier is a group a drafter
 * treats as interchangeable, and the sites and the writers who publish them
 * all draw it at a drop in value rather than at a fixed count. This cuts each
 * position's leading players at its largest gaps — never leaving a tier of
 * one — so a position with three stars and then a cliff reads that way.
 *
 * Players are taken in the order given, which is the board's, and within one
 * position the board is in value order, so a gap is always a drop.
 */
export function positionTiers(
  board: readonly BoardEntry[],
  options: TierOptions = {},
): Map<string, number> {
  const top = options.top ?? 36;
  const tierSize = options.tierSize ?? 5;
  const maxTiers = options.maxTiers ?? 8;

  const byPosition = new Map<string, BoardEntry[]>();
  for (const entry of board) {
    const list = byPosition.get(entry.position) ?? [];
    list.push(entry);
    byPosition.set(entry.position, list);
  }

  const out = new Map<string, number>();
  for (const list of byPosition.values()) {
    const valued = list.filter((e) => e.value !== null && Number.isFinite(e.value));
    const lead = valued.slice(0, top);
    const tiers = Math.min(maxTiers, Math.max(2, Math.round(lead.length / tierSize)));

    // The largest drops, taken greedily, each at least two players from the
    // last cut and from either end, so no tier is a single player.
    const gaps = lead
      .slice(1)
      .map((e, i) => ({ at: i + 1, gap: (lead[i]!.value as number) - (e.value as number) }))
      .sort((a, b) => b.gap - a.gap || a.at - b.at);
    const cuts: number[] = [];
    for (const { at, gap } of gaps) {
      if (cuts.length >= tiers - 1 || gap <= 0) break;
      if (at < 2 || lead.length - at < 2) continue;
      if (cuts.some((c) => Math.abs(c - at) < 2)) continue;
      cuts.push(at);
    }
    cuts.sort((a, b) => a - b);

    let tier = 1;
    lead.forEach((entry, i) => {
      if (cuts.includes(i)) tier += 1;
      out.set(entry.id, tier);
    });
    const rest = lead.length === 0 ? 1 : tier + 1;
    for (const entry of list) if (!out.has(entry.id)) out.set(entry.id, rest);
  }
  return out;
}

export interface ScheduledGame {
  /** The source's week number. */
  week: number;
  home: string;
  away: string;
}

/**
 * Each club's bye: the week of the regular season it does not play.
 *
 * THE REGULAR SEASON IS THE WEEKS MOST CLUBS PLAY, which needs no sport's
 * calendar: a playoff week has a handful of games, a regular one has nearly
 * every club. A club missing from more than one such week (a postponement the
 * schedule has not caught up with) gets the first.
 */
export function byeWeeks(games: readonly ScheduledGame[]): Map<string, number> {
  const clubs = new Set<string>();
  const playing = new Map<number, Set<string>>();
  for (const g of games) {
    clubs.add(g.home);
    clubs.add(g.away);
    const set = playing.get(g.week) ?? new Set<string>();
    set.add(g.home);
    set.add(g.away);
    playing.set(g.week, set);
  }
  const regular = [...playing.entries()]
    .filter(([, set]) => set.size * 2 >= clubs.size)
    .map(([week]) => week)
    .sort((a, b) => a - b);
  const out = new Map<string, number>();
  for (const club of clubs) {
    const week = regular.find((w) => !playing.get(w)!.has(club));
    if (week !== undefined) out.set(club, week);
  }
  return out;
}
